import { Task } from '../types';
import { rowsFromValue } from './stageRows';

export interface IdpAttachment {
  id: string;
  filename: string;
}

const STATUS_NAMES = ['Created', 'WorkflowSelected', 'SLAConfigured', 'Assigned', 'InProgress', 'Completed', 'Overdue', 'Cancelled', 'Escalated'];

export const parseTaskData = (dataJson?: string): Record<string, unknown> => {
  if (!dataJson) return {};
  try {
    const value = JSON.parse(dataJson);
    return value && typeof value === 'object' ? value as Record<string, unknown> : {};
  } catch {
    return {};
  }
};

const textOf = (data: Record<string, unknown>, key: string) =>
  typeof data[key] === 'string' ? (data[key] as string) : '';

export const reviewHeadline = (task: Pick<Task, 'dataJson'>) => {
  const data = parseTaskData(task.dataJson);
  return [textOf(data, 'client'), textOf(data, 'project')].filter(Boolean).join(' · ');
};

export const reviewScope = (task: Pick<Task, 'dataJson'>) => textOf(parseTaskData(task.dataJson), 'scopeSummary');

const HIDDEN_REVIEW_FIELDS = new Set(['source', 'idpPendingReview', 'idpSubmissionId', 'idpAttachments', 'idpAdminUrl']);

const REVIEW_FIELD_ORDER = [
  'enquiryNumber',
  'client',
  'project',
  'contact',
  'contactEmail',
  'contactPhone',
  'sender',
  'receivedOn',
  'requestedSubmission',
  'currentStatus',
  'scopeSummary',
];

/** Every extracted enquiry field, without the internal IDP markers. */
export const reviewFields = (task: Pick<Task, 'dataJson'>): Record<string, unknown> => {
  const data = parseTaskData(task.dataJson);
  const ordered: Record<string, unknown> = {};
  const take = (key: string) => {
    const value = data[key];
    const rows = rowsFromValue(value);
    if (rows) {
      ordered[key] = rows;
      return;
    }
    if (value == null || value === '' || typeof value === 'object') return;
    ordered[key] = value;
  };
  REVIEW_FIELD_ORDER.forEach(take);
  Object.keys(data).forEach((key) => {
    if (HIDDEN_REVIEW_FIELDS.has(key) || key in ordered) return;
    take(key);
  });
  return ordered;
};

export const reviewFieldLabels: Record<string, string> = {
  contactEmail: 'Contact email',
  contactPhone: 'Contact phone',
  currentStatus: 'Current status',
  sender: 'From',
  lineItems: 'Line items',
  line_no: 'Line',
  item_code: 'Item code',
  unit_of_measure: 'Unit',
  target_price: 'Target price',
  required_date: 'Required date',
};

export const idpAttachments = (task: Pick<Task, 'dataJson'>): IdpAttachment[] => {
  const raw = parseTaskData(task.dataJson).idpAttachments;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const row = item as { id?: string; filename?: string };
    if (!row.id) return [];
    return [{ id: row.id, filename: row.filename || 'Attachment' }];
  });
};

/** Review rows come from Task Service, whose status is the enum number. */
export const mapReviewTask = (raw: {
  taskId: string;
  taskName: string;
  description?: string;
  status?: string | number;
  priority?: string;
  dataJson?: string;
  createdByMemberId?: string;
  workflowId?: string;
  currentStageId?: string;
  memberId?: string;
  slaDeadline?: string;
  isOverdue?: boolean;
  createdAt: string;
  updatedAt: string;
}): Task => ({
  taskId: raw.taskId,
  taskName: raw.taskName,
  description: raw.description,
  status: typeof raw.status === 'number' ? (STATUS_NAMES[raw.status] ?? 'Created') : (raw.status || 'Created'),
  priority: raw.priority || 'Medium',
  dataJson: raw.dataJson,
  createdByMemberId: raw.createdByMemberId,
  workflowId: raw.workflowId ?? '',
  stageId: raw.currentStageId,
  stageName: 'Review',
  assignedToMemberId: raw.memberId,
  dueDate: raw.slaDeadline,
  isOverdue: raw.isOverdue,
  createdAt: raw.createdAt,
  updatedAt: raw.updatedAt,
});
